import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Info, Database, Cpu, Cloud, User, FileWarning, CheckCircle2, HelpCircle } from 'lucide-react';

const TYPE_ICON = {
  directo: CheckCircle2, oficial: Database, proveedor: Cloud, calculado: Cpu,
  inferido: HelpCircle, estimacion_ia: Cpu, usuario: User, no_disponible: FileWarning,
};

export default function MethodologyModal({ indexKey, indexName, indexScore, confidence, analysisMeta }) {
  const [open, setOpen] = useState(false);
  const [m, setM] = useState(null);
  const [dataTypes, setDataTypes] = useState({});

  useEffect(() => {
    if (open && !m) {
      api.get(`/methodology/${indexKey}`).then(r => setM(r.data)).catch(() => {});
      api.get('/methodology').then(r => setDataTypes(r.data.data_types || {})).catch(() => {});
    }
  }, [open, indexKey, m]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`methodology-btn-${indexKey}`} className="text-slate-400 hover:text-blue-900 transition-colors" title="¿Cómo se calculó esta calificación?">
          <Info size={13} />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-baseline gap-3">
            <span>¿Cómo se calculó &ldquo;{m?.name || indexName}&rdquo;?</span>
            {indexScore != null && <span className="font-display text-3xl font-bold text-blue-900">{indexScore}<span className="text-sm text-slate-400">/100</span></span>}
          </DialogTitle>
          <DialogDescription>
            Variables, pesos, fuentes y fórmula usados para calcular este índice — para auditoría y trazabilidad.
          </DialogDescription>
        </DialogHeader>
        {!m ? <div className="text-slate-500 text-sm">Cargando metodología…</div> : (
          <div className="space-y-4 text-sm">
            <div className="bg-blue-50 border-l-4 border-blue-900 p-3">
              <div className="label-caps mb-1">Objetivo del indicador</div>
              <div className="text-slate-700">{m.objective}</div>
            </div>

            <div>
              <div className="label-caps mb-2">Variables y pesos</div>
              <table className="w-full text-xs border-collapse">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-2">Variable</th>
                    <th className="text-right p-2">Peso</th>
                    <th className="text-left p-2">Fuente</th>
                    <th className="text-left p-2">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {(m.variables || []).map((v, i) => {
                    const dt = dataTypes[v.data_type] || {};
                    const Icon = TYPE_ICON[v.data_type] || Info;
                    return (
                      <tr key={i} className="border-b border-slate-100" data-testid={`method-var-${i}`}>
                        <td className="p-2 font-semibold text-slate-800">{v.name}</td>
                        <td className="p-2 text-right font-mono text-blue-900">{Math.round(v.weight * 100)}%</td>
                        <td className="p-2 text-slate-600">{v.source}</td>
                        <td className="p-2">
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 font-semibold" style={{ backgroundColor: (dt.color || '#64748b') + '22', color: dt.color || '#64748b' }}>
                            <Icon size={10} /> {dt.label || v.data_type}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <MetaRow label="Fórmula" value={<code className="text-[11px] bg-slate-50 p-2 block whitespace-pre-wrap">{m.formula}</code>} />
              <MetaRow label="Radio / polígono" value={m.radius} />
              <MetaRow label="Nivel geográfico" value={m.geographic_level} />
              <MetaRow label="Confianza" value={<span className="font-mono">{confidence != null ? `${(confidence * 100).toFixed(0)}%` : '—'}</span>} />
              {analysisMeta?.fetched_at && <MetaRow label="Fecha de datos" value={new Date(analysisMeta.fetched_at).toLocaleString('es-MX')} />}
              {m.confidence_notes && <MetaRow label="Nota de confianza" value={<span className="italic text-slate-600 text-xs">{m.confidence_notes}</span>} span2 />}
            </div>

            {m.limitations?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 p-3">
                <div className="label-caps text-amber-900 mb-1 flex items-center gap-1"><FileWarning size={12} />Limitaciones</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-700 text-xs">
                  {m.limitations.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>
            )}

            <details className="text-xs pt-2 border-t border-slate-100">
              <summary className="cursor-pointer text-slate-500 font-semibold">Tipos de dato (leyenda de auditoría)</summary>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(dataTypes).map(([k, v]) => {
                  const Icon = TYPE_ICON[k] || Info;
                  return (
                    <div key={k} className="flex items-start gap-2 p-2 bg-slate-50">
                      <Icon size={12} style={{ color: v.color }} />
                      <div>
                        <div className="font-semibold" style={{ color: v.color }}>{v.label}</div>
                        <div className="text-slate-500 text-[11px]">{v.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const MetaRow = ({ label, value, span2 }) => (
  <div className={`p-2 bg-slate-50 border border-slate-100 ${span2 ? 'col-span-2' : ''}`}>
    <div className="label-caps text-[9px]">{label}</div>
    <div className="mt-1 text-slate-800">{value || '—'}</div>
  </div>
);
